import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import RecordDialog from "@/components/RecordDialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Inbox } from "lucide-react";

export default function DataTable({ opId, entity, schema }) {
  const { user } = useAuth();
  const canWrite = user?.role === "admin" || user?.role === "operador";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, mode: "create", record: null });

  const columns = schema.columns;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/operadores/${opId}/${entity}`);
      setRows(res.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [opId, entity]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => setDialog({ open: true, mode: "create", record: {} });
  const openEdit = (r) => setDialog({ open: true, mode: "edit", record: r });

  const handleSave = async (form) => {
    try {
      if (dialog.mode === "create") {
        await api.post(`/operadores/${opId}/${entity}`, { data: form });
        toast.success("Registro añadido");
      } else {
        const { id, ...rest } = form;
        await api.put(`/operadores/${opId}/${entity}/${dialog.record.id}`, { data: rest });
        toast.success("Registro actualizado");
      }
      setDialog({ open: false, mode: "create", record: null });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleDelete = async (r) => {
    if (!window.confirm("¿Eliminar este registro?")) return;
    try {
      await api.delete(`/operadores/${opId}/${entity}/${r.id}`);
      toast.success("Registro eliminado");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid={`datatable-${entity}`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: "Work Sans" }}>
            {schema.label}
          </h2>
          <p className="text-sm text-muted-foreground">{rows.length} registro(s)</p>
        </div>
        {canWrite && (
          <button
            onClick={openCreate}
            data-testid={`add-${entity}`}
            className="inline-flex items-center gap-2 rounded-md bg-primary text-primary-foreground h-9 px-4 text-sm font-medium shadow-sm hover:bg-primary/90 active:scale-[0.98] transition-all"
          >
            <Plus size={16} /> Añadir
          </button>
        )}
      </div>

      <div className="border border-border rounded-md shadow-sm bg-card overflow-hidden">
        <div className="overflow-x-auto attg-scroll">
          <table className="w-full text-sm text-left">
            <thead>
              <tr className="bg-slate-50 border-b border-border">
                {columns.map((c) => (
                  <th key={c.key} className="px-3 py-2.5 font-semibold text-xs uppercase tracking-wider text-slate-500 whitespace-nowrap">
                    {c.label}
                  </th>
                ))}
                {canWrite && (
                  <th className="px-3 py-2.5 text-right font-semibold text-xs uppercase tracking-wider text-slate-500 sticky right-0 bg-slate-50">
                    Acciones
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={columns.length + (canWrite ? 1 : 0)} className="px-3 py-16 text-center text-muted-foreground">
                    <Inbox className="mx-auto mb-2 text-slate-300" size={32} />
                    {canWrite
                      ? <>No hay registros. Pulsa <span className="font-medium text-slate-600">Añadir</span> para crear el primero.</>
                      : "No hay registros."}
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-border hover:bg-slate-50 transition-colors" data-testid={`row-${entity}`}>
                  {columns.map((c) => (
                    <td key={c.key} className="px-3 py-2 tabular-nums align-middle text-slate-700 whitespace-nowrap max-w-[260px] truncate">
                      {String(r[c.key] ?? "")}
                    </td>
                  ))}
                  {canWrite && (
                    <td className="px-3 py-2 text-right whitespace-nowrap sticky right-0 bg-card">
                      <button onClick={() => openEdit(r)} data-testid="edit-row" className="inline-flex h-7 w-7 items-center justify-center rounded hover:bg-slate-100 text-slate-500 hover:text-primary">
                        <Pencil size={15} />
                      </button>
                      <button onClick={() => handleDelete(r)} data-testid="delete-row" className="inline-flex h-7 w-7 items-center justify-center rounded hover:bg-red-50 text-slate-500 hover:text-destructive ml-1">
                        <Trash2 size={15} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <RecordDialog
        open={dialog.open}
        title={dialog.mode === "create" ? `Añadir · ${schema.label}` : `Editar · ${schema.label}`}
        columns={columns}
        initial={dialog.record}
        onClose={() => setDialog({ open: false, mode: "create", record: null })}
        onSave={handleSave}
      />
    </div>
  );
}
